/**
 * Refine resource → admin/v1/companies API path.
 * Single source of truth so useTable / useShow / useForm all agree.
 * Imported by list.tsx, show.tsx, and edit.tsx.
 */
/**
 * sortFieldOnly: true signals to the data provider that this resource's sort
 * endpoint only accepts field names (no direction prefix). This keeps the data
 * provider generic — no hardcoded path checks.
 */
export const COMPANIES_META = { apiPath: 'admin/v1/companies', sortFieldOnly: true } as const;

/**
 * SF-4578 — what kind of company this is. Values match the sf-api Company model
 * (ACCOUNT_TYPES); sf-api rejects anything else with a 400.
 */
export const ACCOUNT_TYPES = ['Paid', 'Free', 'Demo', 'Test', 'Internal'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

/**
 * SF-5100: ServeFirst's own working company. Stored as "Internal" and always shown as
 * "ServeFirst Live", so the label can change without a data change. sf-api allows only one
 * company to hold it and refuses a second with a 409 (INTERNAL_COMPANY_EXISTS).
 */
export const INTERNAL_ACCOUNT_TYPE: AccountType = 'Internal';
export const INTERNAL_COMPANY_EXISTS = 'INTERNAL_COMPANY_EXISTS';

/** What staff see for each stored account type. */
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  Paid: 'Paid',
  Free: 'Free',
  Demo: 'Demo',
  Test: 'Test',
  Internal: 'ServeFirst Live',
};

/** Display label for a stored account type; an unknown value shows as stored. */
export const accountTypeLabel = (v?: string | null): string =>
  v ? (ACCOUNT_TYPE_LABELS[v as AccountType] ?? v) : '—';

/**
 * SF-5100: sf-api refuses a second ServeFirst Live company with a 409 naming the one that holds
 * it. Say which company that is, so staff know which one to change first. Null = use the
 * server's own message.
 */
export function accountTypeErrorText(err: unknown): string | null {
  const data = (err as { response?: { data?: { code?: unknown; current?: { name?: unknown } } } })
    ?.response?.data;
  if (data?.code !== INTERNAL_COMPANY_EXISTS) return null;
  const name = data.current?.name;
  if (typeof name !== 'string' || !name.trim()) return null;
  const label = ACCOUNT_TYPE_LABELS[INTERNAL_ACCOUNT_TYPE];
  return `${name} is already ${label}, and only one company can be. Change ${name} to another type first.`;
}

/** Picker options for every account type, labelled as staff see them. */
export const ACCOUNT_TYPE_PICKER_OPTIONS = ACCOUNT_TYPES.map((t) => ({
  label: ACCOUNT_TYPE_LABELS[t],
  value: t,
}));

/** SF-4578 — how the company was sold (sf-api SALES_CHANNELS). */
export const SALES_CHANNEL_LABELS = {
  enterprise: 'Enterprise',
  self_serve: 'Self-serve',
} as const;
export type SalesChannel = keyof typeof SALES_CHANNEL_LABELS;
export const SALES_CHANNELS = Object.keys(SALES_CHANNEL_LABELS) as SalesChannel[];

/** Display label for a stored sales channel; a company saved before SF-4578 has none and is enterprise. */
export const salesChannelLabel = (v?: string | null): string =>
  SALES_CHANNEL_LABELS[(v ?? 'enterprise') as SalesChannel] ?? v ?? '—';
